// Restricted arithmetic expression parser for the 'calculated' column type.
// Supports: +, -, *, /, (), numeric literals, and column name identifiers.
// No eval() or Function() — safe for developer-defined formulas.

type TokenType = 'number' | 'identifier' | 'plus' | 'minus' | 'star' | 'slash' | 'lparen' | 'rparen';

interface Token {
    type: TokenType;
    value: string;
}

function tokenize(formula: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;

    while (i < formula.length) {
        const ch = formula[i];

        if (ch === ' ' || ch === '\t' || ch === '\n') { i++; continue; }
        if (ch === '+') { tokens.push({ type: 'plus', value: '+' }); i++; continue; }
        if (ch === '-') { tokens.push({ type: 'minus', value: '-' }); i++; continue; }
        if (ch === '*') { tokens.push({ type: 'star', value: '*' }); i++; continue; }
        if (ch === '/') { tokens.push({ type: 'slash', value: '/' }); i++; continue; }
        if (ch === '(') { tokens.push({ type: 'lparen', value: '(' }); i++; continue; }
        if (ch === ')') { tokens.push({ type: 'rparen', value: ')' }); i++; continue; }

        if ((ch >= '0' && ch <= '9') || ch === '.') {
            let num = '';
            while (i < formula.length && ((formula[i] >= '0' && formula[i] <= '9') || formula[i] === '.')) {
                num += formula[i++];
            }
            tokens.push({ type: 'number', value: num });
            continue;
        }

        if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_') {
            let name = '';
            while (i < formula.length && (
                (formula[i] >= 'a' && formula[i] <= 'z') ||
                (formula[i] >= 'A' && formula[i] <= 'Z') ||
                (formula[i] >= '0' && formula[i] <= '9') ||
                formula[i] === '_'
            )) {
                name += formula[i++];
            }
            tokens.push({ type: 'identifier', value: name });
            continue;
        }

        throw new Error(`Unexpected character '${ch}' at position ${i}`);
    }

    return tokens;
}

// Recursive-descent parser
// expression := term (('+' | '-') term)*
// term       := factor (('*' | '/') factor)*
// factor     := '-' factor | '(' expression ')' | number | identifier

class Parser {
    private tokens: Token[];
    private pos: number = 0;
    private rowData: Record<string, any>;

    constructor(tokens: Token[], rowData: Record<string, any>) {
        this.tokens = tokens;
        this.rowData = rowData;
    }

    private peek(): Token | null {
        return this.pos < this.tokens.length ? this.tokens[this.pos] : null;
    }

    private consume(): Token {
        return this.tokens[this.pos++];
    }

    parseExpression(): number {
        let left = this.parseTerm();
        while (true) {
            const tok = this.peek();
            if (tok?.type === 'plus') { this.consume(); left += this.parseTerm(); }
            else if (tok?.type === 'minus') { this.consume(); left -= this.parseTerm(); }
            else break;
        }
        return left;
    }

    private parseTerm(): number {
        let left = this.parseFactor();
        while (true) {
            const tok = this.peek();
            if (tok?.type === 'star') {
                this.consume();
                left *= this.parseFactor();
            } else if (tok?.type === 'slash') {
                this.consume();
                const divisor = this.parseFactor();
                left = divisor === 0 ? Infinity : left / divisor;
            } else break;
        }
        return left;
    }

    private parseFactor(): number {
        const tok = this.peek();
        if (!tok) throw new Error('Unexpected end of expression');

        if (tok.type === 'minus') {
            this.consume();
            return -this.parseFactor();
        }
        if (tok.type === 'lparen') {
            this.consume();
            const val = this.parseExpression();
            if (this.peek()?.type !== 'rparen') throw new Error('Expected closing parenthesis');
            this.consume();
            return val;
        }
        if (tok.type === 'number') {
            this.consume();
            return parseFloat(tok.value);
        }
        if (tok.type === 'identifier') {
            this.consume();
            const raw = this.rowData[tok.value];
            const num = Number(raw ?? 0);
            return isNaN(num) ? 0 : num;
        }

        throw new Error(`Unexpected token: ${tok.value}`);
    }
}

export function extractColumnRefs(formula: string): string[] {
    try {
        const tokens = tokenize(formula);
        const seen = new Set<string>();
        const refs: string[] = [];
        for (const t of tokens) {
            if (t.type === 'identifier' && !seen.has(t.value)) {
                seen.add(t.value);
                refs.push(t.value);
            }
        }
        return refs;
    } catch {
        return [];
    }
}

export function evaluateFormula(formula: string, rowData: Record<string, any>): number {
    try {
        const tokens = tokenize(formula);
        const parser = new Parser(tokens, rowData);
        return parser.parseExpression();
    } catch {
        return NaN;
    }
}

export function buildSubstitutedFormula(formula: string, rowData: Record<string, any>): string {
    const refs = extractColumnRefs(formula);
    // Sort by length descending so longer names are replaced before shorter ones
    // that may be substrings (e.g. 'approved_rate' before 'rate').
    const sorted = [...refs].sort((a, b) => b.length - a.length);
    let result = formula;
    for (const colName of sorted) {
        const val = Number(rowData[colName] ?? 0);
        const display = isNaN(val) ? '0' : String(parseFloat(val.toFixed(6)));
        result = result.replace(new RegExp(`\\b${colName}\\b`, 'g'), display);
    }
    return result;
}
